import { useAnimationFrame } from "framer-motion";
import { useState, useRef } from "react";
import LottieAnimation from "../LottieAnimation/LottieAnimation";

// Compute a curved Bezier path from one point to another
// Compute a curved or straight position between two points based on progress `t` (0 to 1)
const getCurvedPosition = (t, from, to) => {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const distance = Math.hypot(dx, dy);

  // Use straight line if movement is very short
  const straightThreshold = 10;
  const isStraight =
    Math.abs(dx) < straightThreshold || Math.abs(dy) < straightThreshold;

  if (isStraight) {
    return {
      x: from.x + dx * t,
      y: from.y + dy * t,
    };
  }

  // Midpoint between source and target
  const midX = (from.x + to.x) / 2;
  const midY = (from.y + to.y) / 2;

  // Angle between the points
  const angle = Math.atan2(dy, dx);

  // Perpendicular angle (90 deg to path)
  const perpendicularAngle = angle + Math.PI / 2;

  // Arch height (curve depth)
  const archHeight = distance * 0.25;

  // 👉 Inverted curve: bend away from target direction
  const inverseCurveDirection = dx >= 0 ? -1 : 1;

  // Control point offset from midpoint
  const cx =
    midX + Math.cos(perpendicularAngle) * archHeight * inverseCurveDirection;
  const cy =
    midY + Math.sin(perpendicularAngle) * archHeight * inverseCurveDirection;

  // Quadratic Bézier interpolation
  const x = (1 - t) * (1 - t) * from.x + 2 * (1 - t) * t * cx + t * t * to.x;

  const y = (1 - t) * (1 - t) * from.y + 2 * (1 - t) * t * cy + t * t * to.y;

  return { x, y };
};

const EmojiThrower = ({
  animationSource,
  from = { x: 100, y: 100 },
  to = { x: 300, y: 100 },
  duration = 1.5,
  size = 60,
  loop = false,
  autoPlay = true,
  onComplete = () => {},
}) => {
  const [t, setT] = useState(0);
  const startRef = useRef(null);
  const lottieRef = useRef();

  useAnimationFrame((time) => {
    if (startRef.current === null) {
      startRef.current = time;
      setT(0);
      return;
    }

    const elapsed = (time - startRef.current) / 1000;
    const progress = Math.min(elapsed / duration, 1);

    if (progress < 1) {
      setT(progress);
    } else if (progress >= 1 && t < 1) {
      setT(1);
      if (lottieRef.current && typeof lottieRef.current.play === "function") {
        lottieRef.current.goToAndStop(0, true); // optional reset
        lottieRef.current.play();
      } else {
        console.warn("Lottie player ref not ready or play() unavailable");
      }
      onComplete?.();
    }
  });

  const { x, y } = getCurvedPosition(t, from, to);

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        pointerEvents: "none",
        zIndex: 9999,
      }}
    >
      <div
        style={{
          position: "absolute",
          transform: `translate(${x - size / 2}px, ${y - size / 2}px)`,
          width: size,
          height: size,
        }}
      >
        <LottieAnimation
          animationSource={animationSource}
          size={size}
          loop={false}
          autoPlay={false}
          lottieRef={lottieRef}
        />
      </div>
    </div>
  );
};

export default EmojiThrower;
